import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

type GTMDispatch = (data: Record<string, unknown>) => void;

const GTMContext = createContext<GTMDispatch>(() => {});

export function useGTMDispatch(): GTMDispatch {
  return useContext(GTMContext);
}

interface GTMProviderProps {
  id: string;
  children: ReactNode;
}

/**
 * Lightweight GTM provider replacing @elgorditosalsero/react-gtm-hook.
 * Injects the GTM snippet on mount and exposes dataLayer.push via context.
 */
export function GTMProvider({ id, children }: GTMProviderProps) {
  useEffect(() => {
    if (!id) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [id]);

  const dispatch: GTMDispatch = (data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  };

  return <GTMContext.Provider value={dispatch}>{children}</GTMContext.Provider>;
}
